import { Component } from "react"

/* Shared card component (same visuals as lmp) */
import { Card } from "@cuba-go/poker-ui"


/*
    Hand — one blackjack hand (Pragmatic style): overlapping cards of a
    fixed size (no placeholder slots, no resizing). New cards are added on
    the right; the hand grows to the left from a stable right anchor.
    The dealer hole card flips face-up on reveal.

    Props:
        cards   array of card objects ({ name, uid, hidden })
        score   numeric total
        badge   "bj" | "win" | "lose" | null
        label   caption ("ДИЛЕР", "РУКА 1", ...)
        active  highlight (its turn)
        stand   the hand is locked on stand / double
        surrendered the hand was surrendered (shows a flag mark)
        variant "player" | "dealer"
*/
class Hand extends Component {

    _isHidden = card => !card || card.hidden || !card.name

    _card = (card, index) => {

        const faceUp = !this._isHidden(card)

        return (
            <div className={`bj-hand-card ${faceUp ? "" : "behind"}`} key={card.uid || `${index}`}>
                <div className={`bj-flip ${faceUp ? "active" : ""}`}>
                    <div className="bj-flip-inner">
                        <div className="bj-flip-front">
                            <div className="bj-card-back" />
                        </div>
                        <div className="bj-flip-back">
                            {faceUp ? <Card data={card.name} /> : <div className="bj-card-back" />}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    /* Gold "BJ" badge shown under the score on a blackjack */
    _bjIcon = () => (
        <svg viewBox="0 0 40 42" className="bj-mark-bj" aria-hidden="true">
            <path d="M6 12 L11 18 L15 9 L20 17 L25 9 L29 18 L34 12 L31 24 L9 24 Z" fill="#ffcf33" stroke="#1a1206" strokeWidth="1.6" strokeLinejoin="round" />
            <circle cx="20" cy="27" r="13" fill="#ffcf33" stroke="#1a1206" strokeWidth="2" />
            <text x="20" y="32" textAnchor="middle" fontSize="13" fontWeight="900" fill="#1a1206" fontFamily="Georgia, serif">BJ</text>
        </svg>
    )

    /* Red circle with a line — the hand stood / doubled (locked) */
    _standIcon = () => (
        <svg viewBox="0 0 24 24" className="bj-mark-stand" aria-hidden="true">
            <circle cx="12" cy="12" r="11" fill="#c0392b" stroke="#fff" strokeWidth="1.5" />
            <rect x="6" y="10.4" width="12" height="3.2" rx="1.6" fill="#fff" />
        </svg>
    )

    /* White mushroom-cloud (nuclear blast) mark, no background — the hand
       busted (over 21). Cap with a scalloped bottom + stem + base cloud. */
    _bustIcon = () => (
        <svg viewBox="0 0 48 48" className="bj-mark-bust" aria-hidden="true">
            {/* cap — billowy top, scalloped bottom */}
            <path fill="#fff" d="M9 23.5 C4 23 3.5 15.5 9.2 14.5 C9 9 15 5.8 19.5 9 C22.5 5 31 5.2 33.5 10 C40 9.2 44 15 40 19.5 Q41 21.5 38.5 23 Q36 26 33.8 23 Q31 26 28.6 23 Q25.5 26 22.8 23 Q20 26 17.2 23 Q14 26 11.4 23 Q10 25 9 23.5 Z" />
            {/* stem */}
            <path fill="#fff" d="M20 25 L28 25 C27.2 32 27 39 26.4 45 L21.6 45 C21 39 20.8 32 20 25 Z" />
            {/* base cloud */}
            <path fill="#fff" d="M7 46.5 C7 42.5 12.5 40.5 16 43.2 C18.5 40 29.5 40 32 43.2 C35.5 40.5 41 42.5 41 46.5 C41 48.4 39.6 48.5 38 48.5 L10 48.5 C8.4 48.5 7 48.4 7 46.5 Z" />
        </svg>
    )

    /* White flag — the hand was surrendered (half the stake returned) */
    _surrenderIcon = () => (
        <svg viewBox="0 0 24 24" className="bj-mark-surrender" aria-hidden="true">
            <path d="M6 2 V22" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
            <path d="M6 3 H19 L15.5 7 L19 11 H6 Z" fill="#fff" stroke="#1a1206" strokeWidth="1" strokeLinejoin="round" />
        </svg>
    )

    render = () => {

        const { cards = [], score, badge, label, active, arrow, blackjack, stand, bust, surrendered, variant = "player" } = this.props

        return (
            <div className={`bj-hand bj-hand-${variant} ${active ? "active" : ""} ${badge === "win" ? "win" : ""}`}>

                {label && cards.length > 0 && <div className="bj-hand-label">{label}</div>}

                <div className="bj-hand-box">

                    {cards.length > 0 && (score !== null && score !== undefined) &&
                        <div className="bj-hand-side">
                            <div className={`bj-hand-score ${badge ? `badge-${badge}` : ""}`}>
                                {badge === "bj" ? "BJ" : score}
                            </div>
                            {blackjack && this._bjIcon()}
                            {surrendered && this._surrenderIcon()}
                            {bust && !surrendered && this._bustIcon()}
                            {stand && !bust && !surrendered && this._standIcon()}
                        </div>
                    }

                    <div className="bj-hand-cards">
                        {cards.map((card, index) => this._card(card, index))}
                    </div>

                </div>

                {/* Which hand you are deciding for — animated up-chevron rising */}
                {arrow &&
                    <div className="bj-hand-arrow">
                        <svg viewBox="0 0 28 26" className="bj-hand-arrow-svg" aria-hidden="true">
                            <path d="M4 13 L14 4 L24 13" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M4 22 L14 13 L24 22" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                }

            </div>
        )
    }
}

export default Hand
